import { memo, useEffect, useRef, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useRotatingWords } from "@/hooks/useRotatingWords";
import figurBin from "@/assets/grunder-figur-bin.png";
import { Button } from "@/components/ui/button";
import { GRUNDERLOFTET_AKTIV } from "@/lib/grunderloftet";

const rotatingWords = ["flere kunder", "mer salg", "synlighet", "vekst"];

const HeroSection = () => {
  const [showCampaign, setShowCampaign] = useState(() => {
    try { return GRUNDERLOFTET_AKTIV && window.sessionStorage.getItem("grunderloftet-skjult") !== "1"; }
    catch { return true; }
  });
  const dismissCampaign = () => {
    setShowCampaign(false);
    try { window.sessionStorage.setItem("grunderloftet-skjult", "1"); } catch { /* ignore */ }
  };
  const { currentWord, phase } = useRotatingWords({
    words: rotatingWords,
    interval: 3500,
    animationDuration: 350,
  });

  // Direct DOM scroll handling — avoids React re-renders on every scroll event (jank-free on mobile)
  const innerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip parallax on small screens / reduced-motion devices for smoother performance
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isMobile || reducedMotion) return;

    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      const opacity = y < 100 ? 1 : y > 600 ? 0 : 1 - (y - 100) / 500;
      if (innerRef.current) {
        innerRef.current.style.opacity = String(opacity);
        innerRef.current.style.transform = `translate3d(0, ${y * 0.04}px, 0)`;
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative min-h-[100svh] sm:min-h-screen flex items-center overflow-hidden z-10 bg-gradient-hero">
      {/* Decorative color blobs — lighter blur on mobile to keep GPU happy */}
      <div className="absolute top-20 -left-32 w-72 sm:w-[28rem] h-72 sm:h-[28rem] bg-accent/40 rounded-full blur-2xl sm:blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 -right-32 w-72 sm:w-[28rem] h-72 sm:h-[28rem] bg-emerald-500/35 rounded-full blur-2xl sm:blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-amber-400/35 rounded-full blur-2xl sm:blur-3xl pointer-events-none" />

      <div ref={innerRef} className="container-width relative z-10 will-change-transform">
        <div className="max-w-4xl pt-28 sm:pt-28 pb-8 sm:pb-20">
          <h1
            className="font-display font-extralight text-foreground leading-[1.05] mb-8 sm:mb-10 opacity-0 animate-fade-up tracking-tight text-[clamp(1.6rem,7.6vw,2.5rem)] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem]"
            style={{ animationDelay: "0.15s" }}
          >
            <span className="block">Nettsider som gir deg</span>
            <span className="block relative h-[1.15em] mt-1 sm:mt-2 overflow-hidden">
              <span
                key={currentWord}
                className={`absolute inset-x-0 left-0 text-left text-accent font-medium whitespace-nowrap [transform:translateZ(0)] [backface-visibility:hidden] will-change-[transform,opacity] ${
                  phase === "exiting"
                    ? "animate-word-exit"
                    : phase === "entering"
                    ? "animate-word-enter"
                    : ""
                }`}
              >
                {currentWord}.
              </span>
            </span>
          </h1>

          <p
            className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-xl mb-10 sm:mb-12 opacity-0 animate-fade-up leading-relaxed font-normal"
            style={{ animationDelay: "0.3s" }}
          >
            <span className="text-foreground font-medium">Rask levering.</span>{" "}
            Mobiloptimalisert. Design som faktisk gir deg{" "}
            <span className="text-accent font-medium">flere kunder.</span>
          </p>

          <div
            className="flex flex-wrap items-center gap-3 sm:gap-4 opacity-0 animate-fade-up"
            style={{ animationDelay: "0.45s" }}
          >
            <button
              className="h-12 px-7 text-sm sm:text-base text-accent-foreground bg-accent hover:bg-accent/90 rounded-full transition-all duration-300 font-medium tracking-wide flex items-center gap-2 shadow-md hover:shadow-lg"
              onClick={() => document.getElementById("gratis-utkast")?.scrollIntoView({ behavior: "smooth" })}
            >
              Få et prisforslag
              <ArrowRight size={16} />
            </button>
            <button
              className="h-12 px-7 text-sm sm:text-base text-foreground border border-border hover:border-foreground/40 rounded-full transition-all duration-300 font-medium tracking-wide bg-card"
              onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}
            >
              Kom i gang
            </button>
          </div>
          {showCampaign && (
            <div className="relative mt-9 min-h-20 w-full rounded-lg border border-campaign-lime bg-campaign-deep text-campaign-cream shadow-md sm:hidden">
              <Link
                to="/grunderloftet"
                className="group flex min-h-20 items-center gap-3 overflow-hidden px-4 py-3 pr-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Les om Gründerløftet og søk om en av fem plasser"
              >
                <img src={figurBin} alt="" width={560} height={818} className="-mb-4 w-10 shrink-0 self-end object-contain motion-safe:animate-grunder-peek" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] font-semibold uppercase text-campaign-lime">Kun 5 plasser</span>
                  <span className="block font-display text-base font-bold leading-tight">Gründerløftet</span>
                  <span className="block text-xs leading-snug text-campaign-cream/85">Nettside til 60–90 % under ordinær pris</span>
                </span>
                <ArrowRight size={16} className="shrink-0 text-campaign-lime transition-transform group-hover:translate-x-1" />
              </Link>
              <Button variant="ghost" size="icon" onClick={dismissCampaign} aria-label="Lukk kampanjen" className="absolute right-0 top-0 h-9 w-9 text-campaign-lime hover:bg-campaign-lime/15 hover:text-campaign-lime"><X size={14} /></Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default memo(HeroSection);
